import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, CalendarDays } from "lucide-react";

export function useEvents() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/events").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 1800_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

function countdown(days) {
  if (days <= 0) return "TODAY";
  if (days === 1) return "TOMORROW";
  if (days < 7) return `IN ${days} DAYS`;
  if (days < 14) return "NEXT WEEK";
  return `IN ${days} DAYS`;
}

function ForecastChip({ fc, big }) {
  if (!fc || fc.temp == null) return null;
  const extra = [fc.precip != null ? `${fc.precip}% rain` : null, fc.wind || null].filter(Boolean);
  return (
    <div className={`flex shrink-0 flex-col items-center justify-center rounded-[8px] px-5 text-white ${big ? "py-4" : "py-2"}`} style={{ background: "var(--chip)" }} data-testid="event-forecast-chip">
      <div className={`font-oswald ${big ? "text-[84px]" : "text-[52px]"} font-black leading-none`}>{fc.temp}°</div>
      <div className={`mt-1 max-w-[220px] text-center font-oswald ${big ? "text-[20px]" : "text-[15px]"} font-bold uppercase tracking-wide leading-tight`}>{fc.condition}</div>
      {extra.length ? <div className="mt-1 font-oswald text-[14px] font-semibold uppercase tracking-wider text-white/85">{extra.join(" · ")}</div> : null}
    </div>
  );
}

function EventCard({ ev, hero }) {
  return (
    <div
      className="flex items-center gap-5 rounded-[10px] bg-white px-7 shadow-[0_14px_34px_rgba(10,15,28,0.14)]"
      style={{ borderLeft: `14px solid ${ev.color}`, "--chip": ev.color, flex: hero ? "2 1 0" : "1 1 0" }}
      data-testid={`event-card${hero ? "-hero" : ""}`}
    >
      <div className={`${hero ? "text-[58px]" : "text-[40px]"} leading-none`} aria-hidden>{ev.emoji}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <span className="rounded-[3px] px-3 py-1 font-oswald text-[15px] font-black uppercase tracking-wider text-white" style={{ background: ev.color }}>{ev.category}</span>
          <span className="font-oswald text-[15px] font-black uppercase tracking-[0.2em]" style={{ color: ev.color }}>{countdown(ev.days_until)}</span>
        </div>
        <div className={`mt-2 truncate font-oswald ${hero ? "text-[46px]" : "text-[30px]"} font-black uppercase leading-[0.95] text-[#0a0f1c]`} data-testid="event-title">{ev.title}</div>
        <div className={`mt-1 flex items-center gap-2 font-oswald ${hero ? "text-[22px]" : "text-[17px]"} font-semibold uppercase tracking-wider text-[#5b6675]`}>
          <MapPin className="h-5 w-5 shrink-0" /> <span className="truncate">{ev.venue} · {ev.city}</span>
        </div>
        <div className={`mt-1 font-oswald ${hero ? "text-[22px]" : "text-[16px]"} font-bold uppercase tracking-wide`} style={{ color: ev.color }}>{ev.date_label}</div>
        {hero && ev.blurb ? <div className="mt-2 font-oswald text-[20px] font-medium text-[#3a4452] line-clamp-2">{ev.blurb}</div> : null}
      </div>
      {ev.forecast?.temp != null
        ? <ForecastChip fc={ev.forecast} big={hero} />
        : <div className="shrink-0 max-w-[200px] text-right font-oswald text-[16px] font-semibold uppercase tracking-wide text-[#8a93a2]">{ev.note}</div>}
    </div>
  );
}

export function EventsSlide({ events }) {
  const list = events?.events || [];
  if (!list.length) return null;
  return (
    <div data-testid="events-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      <div className="relative flex h-full w-full flex-col px-[44px] py-[20px]">
        <div className="mb-3 flex items-center gap-2 font-oswald text-[18px] font-bold uppercase tracking-[0.3em] text-[#1f63c9]">
          <CalendarDays className="h-6 w-6" /> Georgia Event Weather
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {list.map((ev, i) => <EventCard key={ev.title + ev.date} ev={ev} hero={i === 0} />)}
        </div>
      </div>
    </div>
  );
}
